// `excel_write_audit_sheet` — pure layout of the Audit sheet (AUDIT_SPEC §6.2, plan "Shared contracts").
// The executor only pours this layout into Excel: a formulas grid from A1, the header / severity
// formats, the column autofit and its free-text exceptions, the frozen rows and the autofilter
// range. No Office.js.
import { colToLetter } from './a1';
import type { AuditReport, AuditReportRow } from '../../types/audit';

export const AUDIT_SHEET_DEFAULT_NAME = 'Audit';
export const AUDIT_COLUMNS = ['Sheet', 'Cell', 'Severity', 'Category', 'Control', 'Message', 'Suggested fix', 'Status'] as const;
/** Free-text columns: a 200-character finding message would autofit off-screen and push every other
 *  column out of view, so they keep a fixed width in character units and wrap instead. */
export const AUDIT_WRAP_COLUMN_WIDTHS: Record<string, number> = { Message: 70, 'Suggested fix': 45 };
/** Column A is sized here rather than by Excel: `autofitColumns` measures the whole column whatever
 *  range it is called on (office-js#5999), so it would size column A to the subtitle in A2. Its width
 *  is computed from the findings alone (rows 5+), clamped, with room for the autofilter arrow. */
export const AUDIT_SHEET_COLUMN_MIN = 10;
export const AUDIT_SHEET_COLUMN_MAX = 40;
export const AUDIT_COLUMN_PADDING = 3;
export const AUDIT_HEADER_ROW = 4;
export const AUDIT_FIRST_DATA_ROW = 5;
export const AUDIT_HEADER_FILL = '#D9E1F2';
export const SEVERITY_FILLS: Record<string, string> = { Blocker: '#F8CBAD', Major: '#FFE699', Minor: '#DDEBF7', Note: '#EDEDED' };
/** A finding that left `open` (retracted or fixed, AUDIT_SPEC §5.4) keeps its severity word but loses its colour:
 *  a withdrawn Blocker must not read as a live one. */
export const RESOLVED_FILL = '#F2F2F2';

export interface AuditFill {
  /** A1 range on the Severity column (consecutive rows of one severity are grouped). */
  address: string;
  color: string;
}

export interface AuditSheetLayout {
  /** Grid written through `range.formulas` from A1: strings, numbers or HYPERLINK formulas. */
  grid: (string | number | null)[][];
  /** Whole written rectangle, `A1:H<last>`. */
  address: string;
  lastRow: number;
  /** Row count written below the header (one per finding). */
  dataRows: number;
  headerAddress: string;
  /** Header + findings, what the autofilter covers (null when there is no finding). */
  tableAddress: string | null;
  titleAddress: string;
  fills: AuditFill[];
  /** Range handed to `autofitColumns`. Columns B..H hold nothing but the table, so Excel measuring
   *  the whole column (office-js#5999) lands on the same width there. */
  autofitAddress: string;
  /** Columns set explicitly, applied after the autofit so they override it: column A (computed from
   *  the findings) and the two free-text columns (fixed and wrapped). */
  fixedColumns: { column: string; width: number; wrap: boolean }[];
  freezeRows: number;
}

/** Excel worksheet name inside a quoted reference: single quotes are doubled. */
function quoteSheet(sheet: string): string {
  return `'${sheet.replace(/'/g, "''")}'`;
}

/** Text safe for `range.formulas`: a leading `=`, `+`, `-` or `@` would be parsed as a formula (a tab or a carriage
 *  return in front of one too: security audit 2026-10-13, AUD-19). */
export function textCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /^[=+\-@\t\r]/.test(s) ? ` ${s}` : s;
}

/** `=HYPERLINK("#'Sheet'!C9","C9")` — jumps to the finding's cell; plain text when there is no cell. */
export function cellLink(sheet: string, cell: string | null): string {
  if (!cell) return '';
  const target = `#${quoteSheet(sheet)}!${cell}`.replace(/"/g, '""');
  const text = cell.replace(/"/g, '""');
  return `=HYPERLINK("${target}","${text}")`;
}

function findingRow(r: AuditReportRow): (string | number | null)[] {
  return [textCell(r.sheet), cellLink(r.sheet, r.cell), textCell(r.severity), textCell(r.category), textCell(r.control_id), textCell(r.message), textCell(r.fix_hint), textCell(r.status)];
}

/** Group consecutive findings of one severity into one fill range on column C. */
function severityFills(rows: readonly AuditReportRow[]): AuditFill[] {
  const out: AuditFill[] = [];
  let run: { color: string; from: number; to: number } | null = null;
  rows.forEach((r, i) => {
    const color = r.status && r.status !== 'open' ? RESOLVED_FILL : SEVERITY_FILLS[String(r.severity)];
    const row = AUDIT_FIRST_DATA_ROW + i;
    if (color && run && run.color === color && run.to === row - 1) {
      run.to = row;
      return;
    }
    if (run) out.push({ address: `C${run.from}:C${run.to}`, color: run.color });
    run = color ? { color, from: row, to: row } : null;
  });
  if (run) out.push({ address: `C${(run as { from: number }).from}:C${(run as { to: number }).to}`, color: (run as { color: string }).color });
  return out;
}

/** Width of column A in character units: the longest sheet name among the findings (rows 5+ — the
 *  title and subtitle in A1/A2 are ignored on purpose), plus room for the autofilter arrow, clamped.
 *  The floor also keeps the `Sheet` header legible when every sheet name is short. */
export function sheetColumnWidth(rows: readonly AuditReportRow[]): number {
  const longest = rows.reduce((max, r) => Math.max(max, textCell(r.sheet).length), 0);
  return Math.min(AUDIT_SHEET_COLUMN_MAX, Math.max(AUDIT_SHEET_COLUMN_MIN, longest + AUDIT_COLUMN_PADDING));
}

/** The complete layout of the Audit sheet for a report. */
export function layoutAuditSheet(report: AuditReport): AuditSheetLayout {
  const cols = AUDIT_COLUMNS.length;
  const blank = (): (string | number | null)[] => Array.from({ length: cols }, () => '');
  const title = blank();
  title[0] = textCell(report.title);
  const subtitle = blank();
  subtitle[0] = textCell(report.subtitle);
  const header = AUDIT_COLUMNS.map((c) => c as string);
  const rows = (report.rows ?? []).map(findingRow);
  const grid = [title, subtitle, blank(), header, ...rows];
  const lastRow = grid.length;
  const lastCol = colToLetter(cols);
  const headerAddress = `A${AUDIT_HEADER_ROW}:${lastCol}${AUDIT_HEADER_ROW}`;
  const tableAddress = rows.length ? `A${AUDIT_HEADER_ROW}:${lastCol}${lastRow}` : null;
  return {
    grid,
    address: `A1:${lastCol}${lastRow}`,
    lastRow,
    dataRows: rows.length,
    headerAddress,
    tableAddress,
    titleAddress: 'A1',
    fills: severityFills(report.rows ?? []),
    autofitAddress: tableAddress ?? headerAddress,
    fixedColumns: [
      { column: 'A', width: sheetColumnWidth(report.rows ?? []), wrap: false },
      ...AUDIT_COLUMNS.map((c, i) => ({ column: colToLetter(i + 1), width: AUDIT_WRAP_COLUMN_WIDTHS[c], wrap: true })).filter((w): w is { column: string; width: number; wrap: boolean } => w.width !== undefined),
    ],
    freezeRows: AUDIT_HEADER_ROW,
  };
}
